import { z } from "zod";

/**
 * Cardiology assessment — structured ECG + echo + NYHA + CV risk factors.
 * Everything is entered as measured EXCEPT QTc, which is derived
 * deterministically from QT + heart rate (Bazett). See computeQtcBazett.
 */

const dateStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD");

export const ecgRhythmSchema = z.enum([
	"sinus",
	"atrial_fibrillation",
	"atrial_flutter",
	"svt",
	"paced",
	"other",
]);
export type EcgRhythm = z.infer<typeof ecgRhythmSchema>;

export const ecgAxisSchema = z.enum(["normal", "left", "right", "extreme"]);
export type EcgAxis = z.infer<typeof ecgAxisSchema>;

export const NYHA_CLASSES = [1, 2, 3, 4] as const;

/** Cardiovascular risk-factor checklist (all optional booleans). */
export const cvRiskFactorsSchema = z.object({
	hypertension: z.boolean().optional(),
	diabetes: z.boolean().optional(),
	smoking: z.boolean().optional(),
	dyslipidemia: z.boolean().optional(),
	family_history: z.boolean().optional(),
	obesity: z.boolean().optional(),
});
export type CvRiskFactors = z.infer<typeof cvRiskFactorsSchema>;

export const cardiacAssessmentApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	visit_id: z.string().uuid().nullable(),
	assessment_date: dateStr,
	nyha_class: z.number().int().min(1).max(4).nullable(),
	bp_systolic: z.number().int().nullable(),
	bp_diastolic: z.number().int().nullable(),
	ecg_rate: z.number().int().nullable(),
	ecg_rhythm: z.string().nullable(),
	pr_ms: z.number().int().nullable(),
	qrs_ms: z.number().int().nullable(),
	qt_ms: z.number().int().nullable(),
	/** Derived (Bazett), computed + stored server-side. */
	qtc_ms: z.number().int().nullable(),
	ecg_axis: z.string().nullable(),
	ecg_interpretation: z.string().nullable(),
	ejection_fraction: z.number().nullable(),
	echo_findings: z.string().nullable(),
	risk_factors: cvRiskFactorsSchema.nullable(),
	notes: z.string().nullable(),
	recorded_by_member_id: z.string().uuid().nullable(),
	created_at: z.string().datetime(),
});
export type CardiacAssessmentApi = z.infer<typeof cardiacAssessmentApiSchema>;

const intIn = (min: number, max: number) =>
	z.coerce.number().int().min(min).max(max).nullable().optional();

export const createCardiacAssessmentInputSchema = z.object({
	patient_id: z.string().uuid(),
	visit_id: z.string().uuid().optional(),
	assessment_date: dateStr,
	nyha_class: z.coerce.number().int().min(1).max(4).nullable().optional(),
	bp_systolic: intIn(40, 300),
	bp_diastolic: intIn(20, 200),
	ecg_rate: intIn(20, 300),
	ecg_rhythm: ecgRhythmSchema.nullable().optional(),
	pr_ms: intIn(40, 600),
	qrs_ms: intIn(40, 400),
	qt_ms: intIn(150, 800),
	ecg_axis: ecgAxisSchema.nullable().optional(),
	ecg_interpretation: z.string().max(2000).nullable().optional(),
	ejection_fraction: z.coerce.number().min(5).max(90).nullable().optional(),
	echo_findings: z.string().max(2000).nullable().optional(),
	risk_factors: cvRiskFactorsSchema.nullable().optional(),
	notes: z.string().max(2000).nullable().optional(),
});
export type CreateCardiacAssessmentInput = z.infer<
	typeof createCardiacAssessmentInputSchema
>;

// ===== Deterministic QTc (Bazett) =====

/**
 * Bazett-corrected QT interval: QTc = QT / √(RR), RR = 60 / heartRate (seconds),
 * with QT/QTc in milliseconds. Pure + deterministic — no clinical judgement.
 * Returns null when inputs are missing/out of plausible range. Bazett is the
 * standard bedside formula; it over-corrects at extreme rates, so the result is
 * a reference (the category thresholds below stay conservative).
 */
export function computeQtcBazett(
	qtMs: number | null | undefined,
	heartRate: number | null | undefined,
): number | null {
	if (qtMs == null || heartRate == null) return null;
	if (!Number.isFinite(qtMs) || !Number.isFinite(heartRate)) return null;
	if (qtMs < 150 || qtMs > 800) return null;
	if (heartRate < 20 || heartRate > 300) return null;
	const rrSeconds = 60 / heartRate;
	const qtc = qtMs / Math.sqrt(rrSeconds);
	return Math.round(qtc);
}

export type QtcCategory = "normal" | "borderline" | "prolonged";

/**
 * Categorise a QTc (ms) by sex. Thresholds: prolonged ≥ 450 (M) / 460 (F);
 * borderline from 430 (M) / 450 (F). A QTc ≥ 500 is high-risk regardless of sex.
 */
export function qtcCategory(
	qtcMs: number | null | undefined,
	sex?: "male" | "female" | string | null,
): QtcCategory | null {
	if (qtcMs == null || !Number.isFinite(qtcMs)) return null;
	if (qtcMs >= 500) return "prolonged";
	const female = sex === "female";
	const prolongedAt = female ? 460 : 450;
	const borderlineAt = female ? 450 : 430;
	if (qtcMs >= prolongedAt) return "prolonged";
	if (qtcMs >= borderlineAt) return "borderline";
	return "normal";
}
